import * as XLSX from 'xlsx';
import { format, parse, isValid, startOfDay, getYear } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { ElectricityData, MonthlyPeak, DailyPeak, HourlyData, QuarterData, AnalysisConfig, AnalysisResult } from '@/types/piekanalyse';

const DUTCH_MONTHS = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
];

// Parse European number format (comma as decimal, dot as thousand separator)
export function parseNumberEU(value: any): number {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  
  const str = String(value).trim();
  // Remove thousand separators (dots) and replace decimal comma with dot
  const normalized = str.replace(/\./g, '').replace(',', '.');
  const num = parseFloat(normalized);
  return isNaN(num) ? 0 : num;
}

// Convert Excel serial date to JS Date
export function parseExcelDate(serial: any): Date | null {
  if (!serial) return null;
  
  // If it's already a Date
  if (serial instanceof Date) return serial;
  
  // If it's a string date
  if (typeof serial === 'string') {
    // Try dd-mm-yyyy hh:mm:ss format
    const parsed = parse(serial, 'dd-MM-yyyy HH:mm:ss', new Date());
    if (isValid(parsed)) return parsed;
    
    // Try other formats
    const parsed2 = parse(serial, 'dd-MM-yyyy HH:mm', new Date());
    if (isValid(parsed2)) return parsed2;
    
    const parsed3 = new Date(serial);
    if (isValid(parsed3)) return parsed3;
  }
  
  // Excel serial number
  if (typeof serial === 'number') {
    const utcDays = Math.floor(serial - 25569);
    const utcValue = utcDays * 86400;
    const fractionalDay = serial - Math.floor(serial);
    const totalSeconds = Math.round(fractionalDay * 86400);
    
    const date = new Date(0);
    date.setUTCSeconds(utcValue + totalSeconds);
    return date;
  }
  
  return null;
}

// Convert kWh to kWp (quarter hour = 0.25 hour)
export function convertKWhToKWp(kWh: number): number {
  return kWh * 4;
}

// Parse Excel file and extract electricity data
export async function parseElectricityData(
  file: File,
  config: AnalysisConfig
): Promise<ElectricityData[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];
        
        const result: ElectricityData[] = [];
        
        // Skip header row
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.length < 3) continue;
          
          const startTime = parseExcelDate(row[0]);
          const endTime = parseExcelDate(row[1]);
          let value = parseNumberEU(row[2]);
          
          if (!startTime) continue;
          
          // Convert kWh to kWp if needed
          if (config.dataType === 'kWh') {
            value = convertKWhToKWp(value);
          }
          
          const dataPoint: ElectricityData = {
            startTime,
            endTime: endTime || startTime,
            kWp: Math.abs(value),
          };
          
          // Parse return delivery if enabled
          if (config.includeReturnDelivery && row[3] !== undefined) {
            let returnValue = parseNumberEU(row[3]);
            if (config.dataType === 'kWh') {
              returnValue = convertKWhToKWp(returnValue);
            }
            dataPoint.returnDelivery = Math.abs(returnValue);
          }
          
          result.push(dataPoint);
        }
        
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

// Calculate monthly peaks
export function calculateMonthlyPeaks(data: ElectricityData[]): MonthlyPeak[] {
  const monthlyMax: Record<number, { kWp: number; returnKWp: number }> = {};
  
  for (let i = 0; i < 12; i++) {
    monthlyMax[i] = { kWp: 0, returnKWp: 0 };
  }
  
  data.forEach(point => {
    const month = point.startTime.getMonth();
    if (point.kWp > monthlyMax[month].kWp) {
      monthlyMax[month].kWp = point.kWp;
    }
    if (point.returnDelivery && point.returnDelivery > monthlyMax[month].returnKWp) {
      monthlyMax[month].returnKWp = point.returnDelivery;
    }
  });
  
  return Object.entries(monthlyMax).map(([month, values]) => ({
    month: parseInt(month),
    monthName: DUTCH_MONTHS[parseInt(month)],
    maxKWp: Math.round(values.kWp * 100) / 100,
    maxReturnKWp: Math.round(values.returnKWp * 100) / 100,
  }));
}

// Calculate daily peaks for a specific month
export function calculateDailyPeaks(data: ElectricityData[], month: number): DailyPeak[] {
  const dailyMax: Record<string, { kWp: number; returnKWp: number; date: Date }> = {};
  
  data
    .filter(point => point.startTime.getMonth() === month)
    .forEach(point => {
      const dateStr = format(point.startTime, 'yyyy-MM-dd');
      
      if (!dailyMax[dateStr]) {
        dailyMax[dateStr] = { kWp: 0, returnKWp: 0, date: startOfDay(point.startTime) };
      }
      
      if (point.kWp > dailyMax[dateStr].kWp) {
        dailyMax[dateStr].kWp = point.kWp;
      }
      if (point.returnDelivery && point.returnDelivery > dailyMax[dateStr].returnKWp) {
        dailyMax[dateStr].returnKWp = point.returnDelivery;
      }
    });
  
  return Object.entries(dailyMax)
    .map(([dateStr, values]) => ({
      date: values.date,
      dateStr: format(values.date, 'd', { locale: nl }),
      maxKWp: Math.round(values.kWp * 100) / 100,
      maxReturnKWp: Math.round(values.returnKWp * 100) / 100,
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

// Find the day with highest consumption
export function findHighestDay(data: ElectricityData[]): { date: Date; data: ElectricityData[] } {
  const dailyTotals: Record<string, { total: number; date: Date; data: ElectricityData[] }> = {};
  
  data.forEach(point => {
    const dateStr = format(point.startTime, 'yyyy-MM-dd');
    
    if (!dailyTotals[dateStr]) {
      dailyTotals[dateStr] = { total: 0, date: startOfDay(point.startTime), data: [] };
    }
    
    if (point.kWp > dailyTotals[dateStr].total) {
      dailyTotals[dateStr].total = point.kWp;
    }
    dailyTotals[dateStr].data.push(point);
  });
  
  let highestDay = { total: 0, date: new Date(), data: [] as ElectricityData[] };
  
  Object.values(dailyTotals).forEach(day => {
    if (day.total > highestDay.total) {
      highestDay = day;
    }
  });
  
  return { date: highestDay.date, data: highestDay.data };
}

// Calculate hourly data for a specific day
export function calculateHourlyData(dayData: ElectricityData[]): HourlyData[] {
  const hourlyMax: Record<number, { kWp: number; returnKWp: number }> = {};
  
  for (let i = 0; i < 24; i++) {
    hourlyMax[i] = { kWp: 0, returnKWp: 0 };
  }
  
  dayData.forEach(point => {
    const hour = point.startTime.getHours();
    if (point.kWp > hourlyMax[hour].kWp) {
      hourlyMax[hour].kWp = point.kWp;
    }
    if (point.returnDelivery && point.returnDelivery > hourlyMax[hour].returnKWp) {
      hourlyMax[hour].returnKWp = point.returnDelivery;
    }
  });
  
  return Object.entries(hourlyMax).map(([hour, values]) => ({
    hour: parseInt(hour),
    hourLabel: `${hour}:00`,
    maxKWp: Math.round(values.kWp * 100) / 100,
    maxReturnKWp: Math.round(values.returnKWp * 100) / 100,
  }));
}

// Count exceedances above contracted capacity
export function countExceedances(data: ElectricityData[], contractedCapacity: number): number {
  return data.filter(point => point.kWp > contractedCapacity).length;
}

// Count months above contract
export function countMonthsAboveContract(monthlyPeaks: MonthlyPeak[], contractedCapacity: number): number {
  return monthlyPeaks.filter(peak => peak.maxKWp > contractedCapacity).length;
}

// Extract daily quarter-hour data for day viewer
export function extractDailyQuarterData(
  data: ElectricityData[]
): Record<string, { quarters: QuarterData[] }> {
  const dailyData: Record<string, { quarters: QuarterData[] }> = {};
  
  data.forEach(point => {
    const dateKey = format(point.startTime, 'yyyy-MM-dd');
    const quarter = Math.floor(
      (point.startTime.getHours() * 60 + point.startTime.getMinutes()) / 15
    );
    const time = format(point.startTime, 'HH:mm');
    
    if (!dailyData[dateKey]) {
      dailyData[dateKey] = { quarters: [] };
    }
    
    dailyData[dateKey].quarters.push({
      quarter,
      time,
      kWp: Math.round(point.kWp * 100) / 100,
      returnKWp: point.returnDelivery ? Math.round(point.returnDelivery * 100) / 100 : undefined,
    });
  });
  
  // Sort quarters within each day
  Object.values(dailyData).forEach(day => {
    day.quarters.sort((a, b) => a.quarter - b.quarter);
  });
  
  return dailyData;
}

// Aggregate quarter data to hourly (max per hour)
export function aggregateQuartersToHourly(quarters: QuarterData[]): HourlyData[] {
  const hourlyMax: Record<number, { kWp: number; returnKWp: number }> = {};
  
  for (let h = 0; h < 24; h++) {
    hourlyMax[h] = { kWp: 0, returnKWp: 0 };
  }
  
  quarters.forEach(q => {
    const hour = Math.floor(q.quarter / 4);
    if (q.kWp > hourlyMax[hour].kWp) {
      hourlyMax[hour].kWp = q.kWp;
    }
    if (q.returnKWp && q.returnKWp > hourlyMax[hour].returnKWp) {
      hourlyMax[hour].returnKWp = q.returnKWp;
    }
  });
  
  return Object.entries(hourlyMax).map(([hour, values]) => ({
    hour: parseInt(hour),
    hourLabel: `${hour.padStart(2, '0')}:00`,
    maxKWp: Math.round(values.kWp * 100) / 100,
    maxReturnKWp: Math.round(values.returnKWp * 100) / 100,
  }));
}

// Detect year from data
export function detectYearFromData(data: ElectricityData[]): number | undefined {
  if (data.length === 0) return undefined;
  
  // Get the most common year in the dataset
  const yearCounts: Record<number, number> = {};
  data.forEach(point => {
    const year = getYear(point.startTime);
    yearCounts[year] = (yearCounts[year] || 0) + 1;
  });
  
  const sortedYears = Object.entries(yearCounts)
    .sort((a, b) => b[1] - a[1]);
  
  return sortedYears.length > 0 ? parseInt(sortedYears[0][0]) : undefined;
}

// Main analysis function
export function analyzeData(data: ElectricityData[], config: AnalysisConfig): AnalysisResult {
  const yearOverview = calculateMonthlyPeaks(data);
  
  const monthlyOverviews: Record<number, DailyPeak[]> = {};
  for (let i = 0; i < 12; i++) {
    monthlyOverviews[i] = calculateDailyPeaks(data, i);
  }
  
  const { date: highestDate, data: highestDayData } = findHighestDay(data);
  const hourlyData = calculateHourlyData(highestDayData);
  
  const highestPeakKWp = Math.max(...data.map(d => d.kWp));
  const monthsAboveContract = countMonthsAboveContract(yearOverview, config.contractedCapacity);
  const totalExceedances = countExceedances(data, config.contractedCapacity);
  
  const hasReturnDelivery = data.some(d => d.returnDelivery && d.returnDelivery > 0);
  
  // Extract raw quarter data for day viewer
  const rawData = extractDailyQuarterData(data);
  const detectedYear = detectYearFromData(data);
  
  return {
    yearOverview,
    monthlyOverviews,
    highestDay: { date: highestDate, hourlyData },
    highestPeakKWp: Math.round(highestPeakKWp * 100) / 100,
    monthsAboveContract,
    totalExceedances,
    hasReturnDelivery,
    rawData,
    detectedYear,
  };
}

// ============ kWh Calculation Functions ============

// Calculate total kWh from quarter data for a single day
// kWp * 0.25 hour = kWh per quarter
export function calculateDailyKWh(quarters: QuarterData[]): number {
  return quarters.reduce((sum, q) => sum + (q.kWp * 0.25), 0);
}

// Calculate total kWh for entire dataset
export function calculateTotalKWh(
  rawData: Record<string, { quarters: QuarterData[] }>
): number {
  return Object.values(rawData)
    .reduce((sum, day) => sum + calculateDailyKWh(day.quarters), 0);
}

// Calculate monthly kWh breakdown
export function calculateMonthlyKWh(
  rawData: Record<string, { quarters: QuarterData[] }>
): Record<number, number> {
  const monthlyKWh: Record<number, number> = {};
  
  for (let i = 0; i < 12; i++) {
    monthlyKWh[i] = 0;
  }
  
  Object.entries(rawData).forEach(([dateStr, dayData]) => {
    const month = parseInt(dateStr.split('-')[1]) - 1; // 0-indexed month
    monthlyKWh[month] += calculateDailyKWh(dayData.quarters);
  });
  
  return monthlyKWh;
}

// Determine day status (green/red) based on threshold
// Threshold = 24h * contracted capacity + battery kWh
export function getDayStatus(
  dailyKWh: number,
  contractedCapacity: number,
  batteryKWh: number = 0
): 'green' | 'red' {
  const threshold = (24 * contractedCapacity) + batteryKWh;
  return dailyKWh <= threshold ? 'green' : 'red';
}

// Calculate threshold value
export function calculateThreshold(
  contractedCapacity: number,
  batteryKWh: number = 0
): number {
  return (24 * contractedCapacity) + batteryKWh;
}

// Generate Excel template
export function generateTemplate(config: AnalysisConfig): void {
  const wb = XLSX.utils.book_new();
  
  const headers = config.includeReturnDelivery
    ? ['Starttijd', 'Eindtijd', config.dataType === 'kWp' ? 'kWp' : 'kWh', 'Teruglevering (kWh)']
    : ['Starttijd', 'Eindtijd', config.dataType === 'kWp' ? 'kWp' : 'kWh'];
  
  const exampleData = config.includeReturnDelivery
    ? [
        ['01-01-2024 00:00:00', '01-01-2024 00:15:00', 150.5, 25.3],
        ['01-01-2024 00:15:00', '01-01-2024 00:30:00', 155.2, 30.1],
        ['01-01-2024 00:30:00', '01-01-2024 00:45:00', 148.8, 28.7],
      ]
    : [
        ['01-01-2024 00:00:00', '01-01-2024 00:15:00', 150.5],
        ['01-01-2024 00:15:00', '01-01-2024 00:30:00', 155.2],
        ['01-01-2024 00:30:00', '01-01-2024 00:45:00', 148.8],
      ];
  
  const ws = XLSX.utils.aoa_to_sheet([headers, ...exampleData]);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 22 },
    { wch: 22 },
    { wch: 12 },
    ...(config.includeReturnDelivery ? [{ wch: 18 }] : []),
  ];
  
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  
  const filename = `Template_${config.dataType}${config.includeReturnDelivery ? '_met_Teruglevering' : ''}.xlsx`;
  XLSX.writeFile(wb, filename);
}
